import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";

export default function SeriesDetail() {
  const { categorySlug, seriesSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const r = useReveal();

  useEffect(() => {
    trackPageView();
    api
      .get(`/series/${seriesSlug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [seriesSlug]);

  return (
    <div data-testid="series-detail-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="max-w-5xl mx-auto px-6 md:px-8">
          <Link
            to="/series"
            data-testid="series-back-link"
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-[#0B1E36]/65 hover:text-[#C5A059] transition-colors mb-12"
          >
            <ArrowLeft size={13} strokeWidth={1.7} />
            Todas as séries
          </Link>

          {loading ? (
            <div className="flex items-center text-[#6B7280] py-20">
              <Loader2 size={18} className="animate-spin mr-2" /> Carregando…
            </div>
          ) : notFound || !data ? (
            <div className="py-20 text-center">
              <p className="font-serif-display text-2xl text-[#0B1E36]">
                Série não encontrada.
              </p>
              <Link
                to="/series"
                className="mt-6 inline-flex items-center gap-2 text-[#C5A059] hover:underline"
              >
                Ver todas as séries
                <ArrowUpRight size={14} />
              </Link>
            </div>
          ) : (
            <>
              <div ref={r} className="reveal max-w-3xl">
                <p className="text-[0.66rem] uppercase tracking-[0.3em] text-[#6B7280]/80 mb-6">
                  Séries / Família Cristã /{" "}
                  <span className="text-[#0B1E36]">{data.title}</span>
                </p>
                <div className="flex items-baseline gap-5">
                  <span className="font-serif-display text-[3.5rem] md:text-[4.5rem] text-[#C5A059] leading-none tabular-nums">
                    {String(data.series_order).padStart(2, "0")}
                  </span>
                  <span className="text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                    {data.sermon_count} episódios
                  </span>
                </div>
                <h1 className="mt-8 font-serif-display text-[#0B1E36] text-3xl md:text-5xl lg:text-[4rem] leading-[1.05] tracking-[-0.028em] font-medium">
                  {data.title}
                </h1>
                <p className="mt-8 text-[1.05rem] md:text-[1.2rem] text-[#6B7280] leading-[1.85] font-light">
                  {data.description}
                </p>
              </div>

              <div className="mt-20 border-t border-[#0B1E36]/10 pt-10">
                <p className="text-[0.66rem] uppercase tracking-[0.3em] text-[#0B1E36]/55 font-medium mb-8">
                  Episódios da série
                </p>
                <ul
                  data-testid="series-episodes-list"
                  className="divide-y divide-[#0B1E36]/8"
                >
                  {data.sermons.map((ep) => (
                    <li key={ep.id}>
                      <Link
                        to={`/pregacoes/${ep.slug}`}
                        data-testid={`series-episode-${ep.episode_number}`}
                        className="group flex items-baseline gap-6 md:gap-8 py-6 hover:bg-white/60 -mx-4 md:-mx-6 px-4 md:px-6 transition-colors duration-200"
                      >
                        <span className="font-serif-display text-[1.6rem] md:text-[2rem] text-[#C5A059] tabular-nums leading-none flex-shrink-0 w-14">
                          E{String(ep.episode_number).padStart(2, "0")}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif-display text-[1.15rem] md:text-[1.35rem] text-[#0B1E36] leading-snug tracking-[-0.014em] group-hover:text-[#C5A059] transition-colors">
                            {ep.title}
                          </h3>
                          {ep.verse_reference && (
                            <p className="mt-2 text-[0.78rem] md:text-[0.85rem] uppercase tracking-[0.2em] text-[#6B7280]/85 font-medium">
                              {ep.verse_reference}
                            </p>
                          )}
                          {ep.preview && (
                            <p className="mt-3 text-[0.92rem] text-[#6B7280] leading-[1.7] font-light line-clamp-2">
                              {ep.preview}
                            </p>
                          )}
                        </div>
                        <ArrowUpRight
                          size={18}
                          strokeWidth={1.5}
                          className="opacity-50 group-hover:opacity-100 group-hover:text-[#C5A059] transition-all duration-300 flex-shrink-0 mt-1"
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
